import { useState, useEffect } from 'react';
import { Search, FileText, X, Send, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchAssignedTeams } from '../../api/chat';
import { fetchIdeas } from '../../api/idea';
import { submitIdeaFeedback } from '../../api/ideaFeedback';
import { toast } from 'sonner';

interface Idea {
  _id: string;
  title: string;
  problem: string;
  solution: string;
  status: string;
  submittedAt: string;
  teamId: string | { _id: string; [key: string]: any };
  hackathonId: { _id: string; title: string } | string;
}

const statusColors: Record<string, string> = {
  submitted_initial:   'bg-blue-100 text-blue-700',
  under_mentor_review: 'bg-yellow-100 text-yellow-700',
  submitted_final:     'bg-purple-100 text-purple-700',
  selected:            'bg-green-100 text-green-700',
  rejected:            'bg-red-100 text-red-700',
};

const statusLabel: Record<string, string> = {
  submitted_initial:   'Submitted',
  under_mentor_review: 'Under Review',
  submitted_final:     'Final Submitted',
  selected:            'Selected',
  rejected:            'Rejected',
};

export function IdeaFeedback() {
  const { token } = useAuth();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }

    fetchAssignedTeams(token)
      .then(async (teams: any[]) => {
        if (teams.length === 0) { setIdeas([]); return; }

        // Fetch ideas for each assigned team in parallel
        const results = await Promise.all(
          teams.map(t => fetchIdeas(token, { teamId: t._id }).catch(() => ({ items: [] })))
        );
        const allIdeas = results.flatMap((r: any) => r.items ?? []);
        setIdeas(allIdeas);
      })
      .catch(() => setIdeas([]))
      .finally(() => setLoading(false));
  }, [token]);

  const filteredIdeas = ideas.filter(idea =>
    idea.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    idea.problem?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openFeedback = (idea: Idea) => {
    setSelectedIdea(idea);
    setComment('');
  };

  const closeModal = () => {
    setSelectedIdea(null);
    setComment('');
  };

  const handleSubmit = async () => {
    if (!comment.trim()) { toast.error('Please enter a comment'); return; }
    if (!token || !selectedIdea) return;

    // Validate all required fields exist
    if (!selectedIdea._id) {
      toast.error('Idea ID is missing');
      return;
    }
    if (!selectedIdea.teamId) {
      toast.error('Team ID is missing from idea');
      return;
    }

    // Extract teamId - it can be either a string or a populated object
    const teamId = typeof selectedIdea.teamId === 'object'
      ? selectedIdea.teamId._id
      : selectedIdea.teamId;

    if (!teamId) {
      toast.error('Team ID is invalid');
      return;
    }

    const hackathonId = typeof selectedIdea.hackathonId === 'object'
      ? selectedIdea.hackathonId._id
      : selectedIdea.hackathonId;

    if (!hackathonId) {
      toast.error('Hackathon ID is missing');
      return;
    }

    setSubmitting(true);
    try {
      await submitIdeaFeedback(token, {
        hackathonId,
        teamId,
        ideaId: selectedIdea._id,
        comment: comment.trim(),
      });
      toast.success('Feedback submitted successfully!');
      closeModal();
    } catch (err: any) {
      console.error('Feedback submission error:', err);
      toast.error(err.message ?? 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full" style={{ minHeight: 0, minWidth: 0 }}>
      <div className="min-h-full bg-[#F8FAFC] px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10" style={{ minHeight: 0, minWidth: 0 }}>
        <div className="mx-auto max-w-6xl space-y-6 sm:space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Idea Feedback</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 sm:mt-2">Review and provide feedback on submitted ideas from your assigned teams</p>
        </div>

        {/* Search */}
        <div className="relative" style={{ minWidth: 0 }}>
          <Search className="absolute left-3 sm:left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 flex-shrink-0" />
            <input
              type="search"
              placeholder="Search by idea title or idea statement..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2 sm:py-3 pl-9 sm:pl-11 text-xs sm:text-sm text-slate-700 shadow-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* Ideas List */}
        <div className="space-y-3 sm:space-y-4" style={{ minWidth: 0 }}>
          {loading ? (
            <div className="rounded-2xl bg-white p-6 sm:p-8 text-center shadow-sm">
              <p className="text-slate-500 text-sm">Loading ideas...</p>
            </div>
          ) : ideas.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 sm:p-8 text-center shadow-sm">
              <p className="text-slate-600 font-medium text-sm">No ideas available yet</p>
              <p className="text-slate-400 text-xs mt-2">Ideas will appear here once your assigned teams submit them.</p>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 sm:p-8 text-center shadow-sm">
              <p className="text-slate-500 text-sm">No ideas match your search</p>
            </div>
          ) : (
            filteredIdeas.map(idea => {
              const hackathonTitle = typeof idea.hackathonId === 'object' ? idea.hackathonId.title : '—';
              return (
                <div key={idea._id} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-md transition" style={{ minWidth: 0 }}>
                  <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-start lg:justify-between" style={{ minWidth: 0 }}>
                    <div className="space-y-2 sm:space-y-3 flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3" style={{ minWidth: 0 }}>
                        <h2 className="text-base sm:text-lg font-semibold text-slate-900 break-words">{idea.title}</h2>
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium flex-shrink-0 whitespace-nowrap ${statusColors[idea.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {statusLabel[idea.status] ?? idea.status}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{idea.problem}</p>
                      <div className="flex flex-col xs:flex-row flex-wrap gap-2 text-xs text-slate-500" style={{ minWidth: 0 }}>
                        <span className="inline-flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full flex-shrink-0">
                          <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{hackathonTitle}</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full flex-shrink-0">
                          <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                          {new Date(idea.submittedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => openFeedback(idea)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white hover:bg-blue-700 transition flex-shrink-0 w-full sm:w-auto whitespace-nowrap"
                    >
                      <Send className="h-4 w-4 flex-shrink-0" />
                      Provide Feedback
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {selectedIdea && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 sm:p-4 z-50 overflow-y-auto" onClick={closeModal}>
          <div className="bg-white rounded-2xl p-5 sm:p-8 max-w-2xl w-full shadow-xl my-8" onClick={e => e.stopPropagation()} style={{ minWidth: 0 }}>
            <div className="flex items-start justify-between mb-4 sm:mb-6 gap-3" style={{ minWidth: 0 }}>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Provide Feedback</h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5 break-words">{selectedIdea.title}</p>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                <X className="h-5 w-5 flex-shrink-0" />
              </button>
            </div>

            {/* Idea summary */}
            <div className="rounded-xl bg-slate-50 p-3 sm:p-4 mb-4 sm:mb-6 space-y-2" style={{ minWidth: 0 }}>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Idea Statement</p>
              <p className="text-xs sm:text-sm text-slate-700 break-words">{selectedIdea.problem}</p>
            </div>

            {/* Comment */}
            <div className="mb-4 sm:mb-6" style={{ minWidth: 0 }}>
              <label className="block text-xs sm:text-sm font-semibold text-slate-900 mb-2">
                Comment <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Provide constructive feedback to help improve this idea..."
                rows={5}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 resize-none"
              />
            </div>

            <div className="flex gap-2 sm:gap-3 justify-end border-t border-slate-100 pt-3 sm:pt-4 flex-col-reverse sm:flex-row">
              <button
                onClick={closeModal}
                className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border border-slate-300 text-slate-700 text-xs sm:text-sm font-medium hover:bg-slate-50 transition flex-shrink-0"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-blue-600 text-white text-xs sm:text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 flex-shrink-0 whitespace-nowrap"
              >
                <Send className="h-4 w-4 flex-shrink-0" />
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
